import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { supabase } from "../lib/supabase";
import { updateAdminPassword, formatApiError } from "../lib/api";
import SEO from "../seo/SEO";

// Users land here from the password-reset email. supabase-js auto-exchanges
// the recovery `code` in the URL for a session (PKCE). We wait for that,
// then let the admin choose a new password and redirect them into the app.
export const UpdatePassword = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | ready | invalid
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data?.session) setStatus("ready");
      else setStatus("invalid");
    };
    check();

    // If the code exchange finishes after mount, react to it.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (session) setStatus("ready");
      else if (event === "SIGNED_OUT") setStatus("invalid");
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await updateAdminPassword(password);
      setDone(true);
      setTimeout(() => navigate("/admin"), 1200);
    } catch (err) {
      setError(formatApiError(err) || "Could not update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-5">
      <SEO title="Set new password — Karen Boley Counselling" noindex />
      <div className="w-full max-w-sm bg-white rounded-2xl border border-cream-2 shadow-xl p-8">
        <h1 className="font-serif text-3xl text-ink">Set a new password</h1>
        <p className="text-sm text-ink-muted mt-1">Choose something strong you&apos;ll remember.</p>

        {status === "checking" && (
          <div data-testid="update-password-checking" className="mt-8 flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-sage" />
          </div>
        )}

        {status === "invalid" && (
          <div data-testid="update-password-invalid" className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-4">
            <p className="text-sm text-ink">
              This reset link is invalid or has expired. Please request a new one.
            </p>
            <Link
              to="/admin/forgot-password"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-sage-dark hover:text-sage"
            >
              Request a new link
            </Link>
          </div>
        )}

        {status === "ready" && !done && (
          <form data-testid="update-password-form" onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wide text-ink-muted mb-1">New password</label>
              <input
                data-testid="update-password-input"
                type="password"
                required
                minLength={8}
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-cream-2 px-3 py-2.5 text-ink outline-none focus:border-sage"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-ink-muted mb-1">Confirm password</label>
              <input
                data-testid="update-password-confirm"
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-lg border border-cream-2 px-3 py-2.5 text-ink outline-none focus:border-sage"
              />
            </div>
            {error && <p data-testid="update-password-error" className="text-sm text-red-600">{error}</p>}
            <button
              data-testid="update-password-submit"
              type="submit"
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sage text-white py-3 font-semibold hover:bg-sage-dark transition-colors disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Update password
            </button>
          </form>
        )}

        {done && (
          <div data-testid="update-password-done" className="mt-6 rounded-xl bg-sage/10 border border-sage/30 p-4 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-sage-dark shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-ink">Password updated</p>
              <p className="text-sm text-ink-soft mt-1">Redirecting you to the dashboard…</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdatePassword;
